import {create} from 'zustand';
import {createJSONStorage, persist} from 'zustand/middleware';
import type {StateStorage} from 'zustand/middleware';

/**
 * Number of days before consent cookies expire.
 * Set to 365 days as per PDPA requirements for consent records.
 */
export const CONSENT_COOKIE_EXPIRY_DAYS = 365;

/** Current privacy policy version. Bump this to require re-consent. */
const CURRENT_POLICY_VERSION = '1.0.0';

interface ConsentPreferences {
  /** Essential cookies - always enabled, required for platform functionality */
  essential: true;
}

interface ConsentState {
  hasResponded: boolean;
  consents: ConsentPreferences;
  consentedAt: string | null;
  policyVersion: string | null;
  _hasHydrated: boolean;
  acknowledge: () => void;
  resetConsent: () => void;
}

const initialConsents: ConsentPreferences = {
  essential: true,
};

/**
 * Cookie storage adapter for Zustand persist middleware.
 * Uses document.cookie directly (no js-cookie dependency).
 */
const cookieStorage: StateStorage = {
  getItem: (name: string): string | null => {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`),
    );
    const value = match?.[1];
    return value !== undefined ? decodeURIComponent(value) : null;
  },
  setItem: (name: string, value: string): void => {
    const expires = new Date();
    expires.setDate(expires.getDate() + CONSENT_COOKIE_EXPIRY_DAYS);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${secure}`;
  },
  removeItem: (name: string): void => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  },
};

export const useConsentStore = create<ConsentState>()(
  persist(
    (set) => ({
      hasResponded: false,
      consents: initialConsents,
      consentedAt: null,
      policyVersion: null,
      _hasHydrated: false,

      acknowledge: () =>
        set({
          hasResponded: true,
          consents: {essential: true},
          consentedAt: new Date().toISOString(),
          policyVersion: CURRENT_POLICY_VERSION,
        }),

      resetConsent: () =>
        set({
          hasResponded: false,
          consents: initialConsents,
          consentedAt: null,
          policyVersion: null,
        }),
    }),
    {
      name: 'consent-storage',
      storage: createJSONStorage(() => cookieStorage),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state._hasHydrated = true;
          // If the policy version changed, require re-consent
          if (state.policyVersion && state.policyVersion !== CURRENT_POLICY_VERSION) {
            state.hasResponded = false;
          }
        }
      },
    },
  ),
);

export function resetConsentStore() {
  useConsentStore.setState({
    hasResponded: false,
    consents: initialConsents,
    consentedAt: null,
    policyVersion: null,
    _hasHydrated: false,
  });
}
